import { isoDateIn, zonedInstant } from '@glory/kernel';

import { type DealState, isIsoDate, type IsoDate, isOpen } from './deal.ts';

/** Что нужно сделать по сделке: позвонить, встретиться, написать, отправить КП или что-то иное. */
export const STEP_KINDS = ['call', 'meeting', 'email', 'proposal', 'other'] as const;
export type StepKind = (typeof STEP_KINDS)[number];

/** Кто поставил шаг, если не человек: автоматика КП (подпроект 3) или документов (отказ от договора, 4.3). */
export const STEP_AUTO_ORIGINS = ['proposal', 'documents'] as const;
export type StepAutoOrigin = (typeof STEP_AUTO_ORIGINS)[number];

export const STEP_TITLE_MAX = 200;
export const STEP_RESULT_MAX = 2_000;

/** Время суток `ЧЧ:ММ`: у шага без времени напоминание приходит в 9:00 его дня. */
export type ClockTime = string;

/** Во сколько приходит напоминание о шаге без времени: 9:00 по поясу студии. */
export const DEFAULT_NOTICE_MINUTES = 9 * 60;

const MINUTES_PER_DAY = 24 * 60;

export const isClockTime = (value: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

/** Минуты от полуночи по `ЧЧ:ММ` или `ЧЧ:ММ:СС` (так отдаёт база); не время — null. */
export function clockMinutes(value: string): number | null {
  const short = value.length === 8 && value.endsWith(':00') ? value.slice(0, 5) : value;
  if (!isClockTime(short)) return null;
  return Number(short.slice(0, 2)) * 60 + Number(short.slice(3, 5));
}

/** `ЧЧ:ММ` по минутам от полуночи. */
export function clockText(minutes: number): ClockTime {
  const clamped = Math.min(Math.max(Math.trunc(minutes), 0), MINUTES_PER_DAY - 1);
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}

/** Когда шаг: день и, по желанию, время — всё по часовому поясу студии. */
export interface StepTiming {
  readonly dueDate: IsoDate;
  /** Null — шаг на весь день. */
  readonly dueTime: ClockTime | null;
}

/** День плюс сколько-то дней: даты ISO считаются в UTC, без часовых поясов. */
export function addDays(date: IsoDate, days: number): IsoDate {
  const moved = new Date(`${date}T00:00:00Z`);
  moved.setUTCDate(moved.getUTCDate() + days);
  return moved.toISOString().slice(0, 10);
}

/**
 * Момент, когда шаг наступает: указанное время, а у шага без времени — 9:00 его дня. Это же момент
 * уведомления исполнителю.
 */
export function stepMoment(step: StepTiming, timeZone: string): Date {
  const minutes = step.dueTime === null ? null : clockMinutes(step.dueTime);
  return zonedInstant(timeZone, step.dueDate, minutes ?? DEFAULT_NOTICE_MINUTES);
}

/**
 * Просрочен: у шага со временем — время прошло; у шага без времени — день закончился (просрочка
 * начинается после конца дня, а не в 9:00).
 */
export function stepIsOverdue(step: StepTiming, now: Date, timeZone: string): boolean {
  if (step.dueTime === null) return isoDateIn(timeZone, now) > step.dueDate;
  const minutes = clockMinutes(step.dueTime);
  if (minutes === null) return false;
  return now.getTime() > zonedInstant(timeZone, step.dueDate, minutes).getTime();
}

/** Где шаг относительно сегодняшнего дня студии. */
export type StepStatus = 'overdue' | 'today' | 'upcoming';

/** «Просрочен» важнее «на сегодня»: шаг на сегодня в 10:00, которого в 11:00 не сделали, просрочен. */
export function stepStatus(step: StepTiming, now: Date, timeZone: string): StepStatus {
  if (stepIsOverdue(step, now, timeZone)) return 'overdue';
  return step.dueDate === isoDateIn(timeZone, now) ? 'today' : 'upcoming';
}

/** «На сегодня»: день шага — сегодня по поясу студии и он ещё не просрочен. */
export const stepIsToday = (step: StepTiming, now: Date, timeZone: string): boolean =>
  stepStatus(step, now, timeZone) === 'today';

/** Пора напомнить: момент шага наступил, а напоминания ещё не было. */
export function stepNoticeDue(
  step: StepTiming & { readonly notifiedAt: Date | null },
  now: Date,
  timeZone: string,
): boolean {
  return step.notifiedAt === null && stepMoment(step, timeZone).getTime() <= now.getTime();
}

// --- проверки -------------------------------------------------------------------------------------

export type StepIssue =
  'kind_invalid' | 'title_required' | 'title_too_long' | 'date_invalid' | 'time_invalid' | 'result_too_long';

export interface StepInput {
  readonly kind: string;
  readonly title: string | null | undefined;
  readonly dueDate: string;
  readonly dueTime?: string | null | undefined;
}

export const isStepKind = (value: string): value is StepKind =>
  (STEP_KINDS as readonly string[]).includes(value);

/** Название шага: лишние пробелы убираются, пустое — null. */
export function normalizeStepTitle(value: string | null | undefined): string | null {
  const clean = (value ?? '').replace(/\s+/g, ' ').trim();
  return clean === '' ? null : clean;
}

export function normalizeStepResult(value: string | null | undefined): string | null {
  const clean = (value ?? '').trim();
  return clean === '' ? null : clean;
}

/** Время из поля ввода: `ЧЧ:ММ` или `ЧЧ:ММ:СС` → `ЧЧ:ММ`; пусто — null; остальное — null и ошибка в проверке. */
export function normalizeDueTime(value: string | null | undefined): ClockTime | null {
  if (value === null || value === undefined || value.trim() === '') return null;
  const minutes = clockMinutes(value.trim());
  return minutes === null ? null : clockText(minutes);
}

/** Что не так с шагом: вид, название, дата и время. Пусто — можно сохранять. */
export function stepIssues(input: StepInput): StepIssue[] {
  const issues: StepIssue[] = [];
  if (!isStepKind(input.kind)) issues.push('kind_invalid');
  const title = normalizeStepTitle(input.title);
  if (title === null) issues.push('title_required');
  else if (title.length > STEP_TITLE_MAX) issues.push('title_too_long');
  if (!isIsoDate(input.dueDate)) issues.push('date_invalid');
  const time = input.dueTime;
  if (time !== null && time !== undefined && time.trim() !== '' && normalizeDueTime(time) === null)
    issues.push('time_invalid');
  return issues;
}

/** Выполненный шаг — история: править, переносить и удалять можно только открытый. */
export const canEditStep = (step: { readonly doneAt: Date | null }): boolean => step.doneAt === null;

/** Шаги ставят и меняют у открытой записи: у закрытой шага нет смысла. */
export const canSetStep = (deal: Pick<DealState, 'outcome'>): boolean => isOpen(deal);

// --- быстрые даты -----------------------------------------------------------------------------------

/** «Завтра», «Через 3 дня», «Через неделю»: так же быстро, как «Позже» в мессенджере. */
export const STEP_PRESETS = [
  { key: 'tomorrow', days: 1 },
  { key: 'in3days', days: 3 },
  { key: 'inWeek', days: 7 },
] as const;
export type StepPresetKey = (typeof STEP_PRESETS)[number]['key'];

/** Быстрые даты от сегодняшнего дня студии. */
export function stepPresets(today: IsoDate): { key: StepPresetKey; date: IsoDate }[] {
  return STEP_PRESETS.map(({ key, days }) => ({ key, date: addDays(today, days) }));
}

// --- касание из выполненного шага --------------------------------------------------------------------

/** Звонок и встреча, выполненные как шаг, сами попадают в журнал касаний; остальные шаги — нет. */
export function touchKindOfStep(kind: StepKind): 'call' | 'meeting' | null {
  return kind === 'call' || kind === 'meeting' ? kind : null;
}

// --- «Сделка без шага» -------------------------------------------------------------------------------

/**
 * Запись без шага: открытая, не отложенная, взятая (лид в очереди ничей — о нём напоминает таймер
 * очереди) и без открытого шага. Этим подсвечиваются карточки и набирается утренняя сводка.
 */
export function isWithoutStep(
  deal: Pick<DealState, 'outcome' | 'postponedUntil' | 'phase' | 'leadState'>,
  hasOpenStep: boolean,
): boolean {
  if (!isOpen(deal) || deal.postponedUntil !== null || hasOpenStep) return false;
  return !(deal.phase === 'lead' && deal.leadState === 'queued');
}

/**
 * Следить за шагами можно, только пока запись в работе: у закрытой и отложенной шаги молчат (не
 * напоминают и не числятся просроченными), у отложенной — до дня возврата.
 */
export const stepsAreLive = (deal: Pick<DealState, 'outcome' | 'postponedUntil'>): boolean =>
  isOpen(deal) && deal.postponedUntil === null;
